import { useCallback, useEffect, useRef, useState } from 'react';

export type CopyStatus = 'idle' | 'copied' | 'error';

const DEFAULT_RESET_MS = 2000;

type UseCopyToClipboardOptions = {
  resetMs?: number;
};

export type UseCopyToClipboardResult = {
  status: CopyStatus;
  copy: (text: string) => Promise<boolean>;
};

async function writeWithClipboardApi(text: string): Promise<boolean> {
  if (!navigator.clipboard?.writeText) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function writeWithExecCommand(text: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  try {
    document.body.appendChild(textarea);
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    if (textarea.parentNode) {
      document.body.removeChild(textarea);
    }
  }
}

async function copyToSystemClipboard(text: string): Promise<boolean> {
  if (await writeWithClipboardApi(text)) {
    return true;
  }
  return writeWithExecCommand(text);
}

export function useCopyToClipboard(
  options?: UseCopyToClipboardOptions,
): UseCopyToClipboardResult {
  const resetMs = options?.resetMs ?? DEFAULT_RESET_MS;
  const [status, setStatus] = useState<CopyStatus>('idle');
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearResetTimer = useCallback(() => {
    if (resetTimerRef.current !== null) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return clearResetTimer;
  }, [clearResetTimer]);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      clearResetTimer();
      let success = false;
      try {
        success = await copyToSystemClipboard(text);
      } catch {
        success = false;
      }
      if (success) {
        setStatus('copied');
        resetTimerRef.current = setTimeout(() => {
          setStatus('idle');
          resetTimerRef.current = null;
        }, resetMs);
        return true;
      }
      setStatus('error');
      return false;
    },
    [clearResetTimer, resetMs],
  );

  return { status, copy };
}
